import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Book,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocShareAccess = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessGranted, setAccessGranted] = useState(false);

  const handleAccess = async (e) => {
    e.preventDefault();
    
    if (!password) {
      setError('Veuillez entrer le mot de passe');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/doc-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessGranted(true);
        toast.success('Accès accordé !');
        
        // Store session token and redirect to doc viewer
        sessionStorage.setItem('doc_session', data.session_token);
        sessionStorage.setItem('doc_token', token);
        
        // Redirect to documentation after a brief delay
        setTimeout(() => {
          navigate('/doc-share-view');
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Accès refusé');
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-[hsl(220,17%,10%)]' : 'bg-gradient-to-br from-slate-100 to-indigo-100'}`}>
      <Card className={`w-full max-w-md ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
        <CardHeader className="text-center pb-2">
          <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
            <Book className={`h-8 w-8 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          </div>
          <CardTitle className={`text-2xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Documentation B2RCS
          </CardTitle>
          <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
            Ce contenu est protégé par mot de passe
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {accessGranted ? (
            <div className={`text-center p-6 rounded-xl ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <p className={`font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                Accès accordé !
              </p>
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Redirection vers la documentation...
              </p>
            </div>
          ) : (
            <form onSubmit={handleAccess} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className={`flex items-center gap-2 p-3 rounded-xl ${isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}`}>
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              {/* Password Input */}
              <div className="space-y-2">
                <Label className={isDarkMode ? 'text-slate-300' : ''}>
                  <Lock className="h-4 w-4 inline mr-2" />
                  Mot de passe
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Entrez le mot de passe"
                    className={`pr-10 h-12 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 font-semibold"
              >
                {loading ? (
                  'Vérification...'
                ) : (
                  <>
                    Accéder à la documentation
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
              
              {/* Info */}
              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Ce lien a été partagé par un administrateur B2RCS.
                <br />
                Contactez l'expéditeur si vous n'avez pas le mot de passe.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocShareAccess;
